<script>
export default {
    name: 'BaseCalendar',
};
</script>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { VIEW } from '@/components/base-components/components/calendar/index.js';
import BaseCalendarNavigation from '@/components/base-components/components/calendar/components/navigation/index.vue';
import BaseCalendarViewDay from '@/components/base-components/components/calendar/components/view/day.vue';
import BaseCalendarViewMonth from '@/components/base-components/components/calendar/components/view/month.vue';
import BaseCalendarViewSchedule from '@/components/base-components/components/calendar/components/view/schedule.vue';
import BaseCalendarViewWeek from '@/components/base-components/components/calendar/components/view/week.vue';
import BaseCalendarViewYear from '@/components/base-components/components/calendar/components/view/year.vue';

const props = defineProps({
    value: {
        type: Date,
    },
    view: {
        type: String,
        default: VIEW.MONTH,
        validator: value => Object.values(VIEW).includes(value),
    },
    firstDayOfWeek: {
        type: Number,
        default: 1,
    },
    noToday: {
        type: Boolean,
    },
    noWeekends: {
        type: Boolean,
    },
    formatterOptions: {
        type: Object,
    },
    titleClass: {
        type: [String, Array, Object],
    },
    wrapperClass: {
        type: [String, Array, Object],
    },
    gridClass: {
        type: [String, Array, Object],
    },
    disabled: {
        type: Boolean,
    },
});

const emit = defineEmits({
    input: null,
    range: null,
});

const date = ref(props.value ?? new Date());

watch(() => date.value, () => {
    emit('input', date.value);
});

const viewComponent = computed(() => ({
    [VIEW.DAY]: BaseCalendarViewDay,
    [VIEW.WEEK]: BaseCalendarViewWeek,
    [VIEW.MONTH]: BaseCalendarViewMonth,
    [VIEW.YEAR]: BaseCalendarViewYear,
    [VIEW.SCHEDULE]: BaseCalendarViewSchedule,
}[props.view]));

const range = ref(null);

function onRange(value) {
    range.value = value;
}

watch(() => range.value, () => {
    const [from, ...rest] = range.value;
    const [to] = rest.toReversed();

    emit('range', { from, to });
}, { deep: true });

const wrapperRef = ref(null);

async function scrollToElement(selector) {
    await nextTick();

    requestAnimationFrame(() => {
        if (!wrapperRef.value) return;

        const target = wrapperRef.value?.querySelector(selector);

        if (!target) return;

        target.scrollIntoView({
            block: 'start',
            inline: 'start',
            behavior: 'smooth',
        });
    });
}

defineExpose({
    scrollToElement,
    wrapperRef,
});
</script>

<template>
    <div class="flex flex-1 flex-col">
        <base-calendar-navigation
            v-model="date"
            :view="view"
            :first-day-of-week="firstDayOfWeek"
            :formatter-options="formatterOptions"
            :no-today="noToday"
            :no-weekends="noWeekends"
            :disabled="disabled"
        >
            <template #default="scope">
                <slot name="navigation" v-bind="scope" />
            </template>
        </base-calendar-navigation>

        <div
            ref="wrapperRef"
            class="relative flex-1 flex flex-col"
            :class="wrapperClass"
        >
            <component
                class="flex-1"
                :is="viewComponent"
                :title-class="titleClass"
                :grid-class="gridClass"
                :date="date"
                :first-day-of-week="firstDayOfWeek"
                :no-weekends="noWeekends"
                @range="onRange"
            >
                <template #title="titleScope">
                    <slot name="title" v-bind="titleScope" :view="view" />
                </template>

                <template #cell="cellScope">
                    <slot name="cell" v-bind="cellScope" :view="view" />
                </template>

                <template #pointer-cell="cellScope">
                    <slot name="pointer-cell" v-bind="cellScope" :view="view" />
                </template>

                <template #content="contentScope">
                    <slot name="content" v-bind="contentScope" :view="view" />
                </template>
            </component>
        </div>
    </div>
</template>
